import { useEffect, useState } from 'react';
import {
  Campaign,
  CreateCampaignRequest,
  Status_Enum as CampaignStatus,
  CampaignType_Enum as CampaignType,
  UpdateCampaignRequest,
} from '@weave/schema-gen-ts/dist/schemas/messaging/bulk/v2';
import { useCreateCampaign, useUpdateCampaign } from '@frontend/api-bulk-messaging';
import { useFormatDynamicFieldAndLabel } from '@frontend/dynamic-fields-hooks';
import { useAppScopeStore } from '@frontend/scope';
import { useUnsavedChanges } from '@frontend/unsaved-changes-modal';
import { useModalControl } from '@frontend/design-system';
import { createUpdateMask } from '../utils/create-update-mask';

// TODO: figure out a way to make this pattern dynamic so we don't have to build this regex ourselves especially if we need to add more routes in the future
const EMAIL_ALLOWED_ROUTE_PATTERN =
  /^\/bulk\/(all-campaigns|drafts)\/email\/(new|[0-9a-fA-F-]+)(\/(?:editor|compose|templates|new-email))?(\/.*)?$/;

export const useCampaignSave = (campaignType: CampaignType, originalCampaign?: Campaign) => {
  const isCreate = originalCampaign === undefined;
  const { selectedOrgId } = useAppScopeStore();
  const { mutateAsync: createCampaign } = useCreateCampaign(campaignType);
  const { mutateAsync: updateCampaign } = useUpdateCampaign(campaignType);
  const { convertLabelToDynamicField } = useFormatDynamicFieldAndLabel();
  // keeps track of the last saved state for the comparison when currentStatus is updated and the user stays on the same page (e.g. UNSPECIFIED => HIDDEN_DRAFT and HIDDEN_DRAFT => SCHEDULED)
  const [lastSavedCampaign, setLastSavedCampaign] = useState(originalCampaign);

  useEffect(() => {
    setLastSavedCampaign(originalCampaign);
  }, [originalCampaign]);

  const [changesExist, setChangesExist] = useState(false);
  const { modalProps: unsavedChangesModalProps, triggerProps: unsavedChangesTriggerProps } = useModalControl();

  // email campaigns have additional routes that the user can navigate to without losing their unsaved changes while text campaigns stay within the editor view
  const allowedRoutePattern = campaignType === CampaignType.EMAIL ? EMAIL_ALLOWED_ROUTE_PATTERN : undefined;
  const { proceed } = useUnsavedChanges(
    changesExist,
    unsavedChangesModalProps.show,
    unsavedChangesTriggerProps.onClick,
    allowedRoutePattern
  );

  const saveAs = async (state: Campaign, saveAsStatus: CampaignStatus) => {
    const updatedCampaign = {
      ...state,
      currentStatus: saveAsStatus,
    };

    // Attachments currently only support one file
    const attachmentMediaIds =
      state.locationIds?.length === 1 && state.attachmentMediaIds?.length
        ? state.attachmentMediaIds.slice(0, 1)
        : undefined;

    // The subject field contains readable labels and needs to be converted to dynamic fields so the backend can interpolate the values
    const subject = convertLabelToDynamicField(state.subject);

    const request: CreateCampaignRequest | UpdateCampaignRequest = {
      orgId: selectedOrgId,
      campaign: {
        ...updatedCampaign,
        attachmentMediaIds,
        subject,
      },
    };

    let campaignId: string | undefined;

    if (isCreate && !request.campaign?.campaignId) {
      const response = await createCampaign(request);
      campaignId = response.campaignId;
    } else {
      const updateMask = createUpdateMask(lastSavedCampaign, updatedCampaign);

      if (updateMask.paths.length === 0) {
        // The updateMask is empty which means this campaign exists on the backend exactly like it is on the frontend.
        // Attempting to save it will result in an error. Bypass the backend update and return the current campaignId.
        campaignId = lastSavedCampaign?.campaignId;
      } else {
        const response = await updateCampaign({
          ...request,
          updateMask,
        });
        campaignId = response.campaignId;
      }
    }

    if (!campaignId) {
      throw new Error('A campaign id was not returned from the server');
    }

    setLastSavedCampaign(updatedCampaign);
    return campaignId;
  };

  return {
    lastSavedCampaign,
    saveAs,
    unsavedChangesProps: {
      modalProps: unsavedChangesModalProps,
      proceed,
      setChangesExist,
    },
  };
};

/*
  These are the different cases for the campaign statuses and the actions that can be taken for each status:
  Status => Actions => Create or Update or N/A

  CampaignStatus_Enum.DELETED => can't access this in the UI => N/A
  
  CampaignStatus_Enum.CANCELED => schedule (update title) => Update
  CampaignStatus_Enum.COMPLETED => schedule (update title) => Update
  CampaignStatus_Enum.FAILED => schedule (update title) => Update
  CampaignStatus_Enum.PROCESSING => schedule (update title) => Update
  CampaignStatus_Enum.SCHEDULED => draft, schedule => Update
  
  CampaignStatus_Enum.DRAFT => draft, schedule => Create
  CampaignStatus_Enum.HIDDEN_DRAFT => draft, schedule => Create
  CampaignStatus_Enum.CAMPAIGN_STATUS_UNSPECIFIED => draft, hidden, schedule => Create (but honestly it should never hit this case)
*/
